import Popup from '@shared/components/Popup/Popup';
import { ReactNode, useContext } from 'react';

import CloseButton from 'components/CloseButton';
import GradientIconWithShadow from 'components/GradientIconWithShadow/GradientIconWithShadow';
import { IconType } from 'utils/types';

import { CreateSecurityCodeDataContext } from './CreateSecurityCodeContext';

interface SecurityCodeModalBaseProps {
  isVisible: boolean;
  onClose: () => void;
  icon: IconType;
  title: string;
  children: ReactNode;
  footer: ReactNode;
  /** When false, hides the top-right X button (Cancel in footer may still be available). */
  showCloseButton?: boolean;
}

const SecurityCodeModalBase = ({
  isVisible,
  onClose,
  icon,
  title,
  children,
  footer,
  showCloseButton = true,
}: SecurityCodeModalBaseProps) => {
  const createSecurityCodeContext = useContext(CreateSecurityCodeDataContext);
  const enforced = createSecurityCodeContext?.enforced ?? false;

  return (
    <Popup isVisible={isVisible} closePopup={enforced || !showCloseButton ? undefined : onClose}>
      <Popup.FullScreenContainer className="relative flex min-h-0 flex-col border border-gray-30 !bg-gray-10 md:w-[512px] md:min-w-[512px] md:rounded-2xl md:max-h-[90dvh]">
        {showCloseButton && !enforced && (
          <div className="absolute right-[15px] top-[15px] z-10">
            <CloseButton onClick={onClose} className="!bg-gray-10" />
          </div>
        )}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-6 pt-12 pb-6">
          <div className="flex flex-col items-center gap-8">
            <GradientIconWithShadow icon={icon} bgVariant="gray" iconClassName="stroke-1.5 text-primary-70" />
            <p className="text-center text-xl font-semibold leading-[1.25] text-black">{title}</p>
            {children}
          </div>
        </div>
        <div className="shrink-0 border-t border-gray-20 px-6 py-4">
          <div className="flex w-full items-center justify-end gap-4">{footer}</div>
        </div>
      </Popup.FullScreenContainer>
    </Popup>
  );
};

export default SecurityCodeModalBase;
